import { useState, useEffect } from 'react'
import { Loader2, Lock, CheckCircle2, RotateCcw, Eye, AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { applyWorkflowTransition, loadChapterAuditLog } from '../utils/chapterWorkflowEngine'
import { useChapterStore } from '../store/chapterStore'
import toast from 'react-hot-toast'

// ── Status display config ─────────────────────────────────────────────────────

export const CHAPTER_STATUS = {
  NOT_STARTED:  { label: 'Not Started',   color: '#94a3b8', bg: 'rgba(226,232,240,0.5)', border: '#e2e8f0' },
  DRAFT:        { label: 'Draft',         color: '#2563eb', bg: 'rgba(37,99,235,0.08)',  border: 'rgba(37,99,235,0.3)' },
  GENERATING:   { label: 'Generating',    color: '#2563eb', bg: 'rgba(37,99,235,0.06)',  border: 'rgba(37,99,235,0.3)', pulse: true },
  REVIEWED:     { label: 'Reviewed',      color: '#0891b2', bg: 'rgba(8,145,178,0.08)',  border: 'rgba(8,145,178,0.3)' },
  REVIEW:       { label: 'Under Review',  color: '#d97706', bg: 'rgba(217,119,6,0.08)',  border: 'rgba(217,119,6,0.3)' },
  UNDER_REVIEW: { label: 'Under Review',  color: '#d97706', bg: 'rgba(217,119,6,0.08)',  border: 'rgba(217,119,6,0.3)' },
  APPROVED:     { label: 'Approved',      color: '#16a34a', bg: 'rgba(22,163,74,0.08)',  border: 'rgba(22,163,74,0.3)' },
  FINALIZED:    { label: 'Finalized',     color: '#15803d', bg: 'rgba(21,128,61,0.10)',  border: 'rgba(21,128,61,0.4)' },
  REGEN_PENDING:{ label: 'Regen Pending', color: '#dc2626', bg: 'rgba(220,38,38,0.08)',  border: 'rgba(220,38,38,0.3)' },
  LOCKED:       { label: 'Locked',        color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', border: 'rgba(124,58,237,0.3)' },
}

// ── State machine transition definitions ──────────────────────────────────────

const TRANSITIONS = {
  DRAFT:        [{ label: 'Submit for Review', next: 'UNDER_REVIEW', icon: Eye,          confirm: false }],
  REVIEWED:     [{ label: 'Submit for Review', next: 'UNDER_REVIEW', icon: Eye,          confirm: false }],
  REVIEW:       [
    { label: 'Submit for Review', next: 'UNDER_REVIEW', icon: Eye,          confirm: false },
    { label: 'Back to Draft',     next: 'DRAFT',         icon: RotateCcw,    confirm: false },
  ],
  UNDER_REVIEW: [
    { label: 'Approve Chapter',   next: 'APPROVED',      icon: CheckCircle2, confirm: true,
      confirmMsg: 'Approving this chapter stabilises it for export. Minor edits such as punctuation remain permitted. You may return to Draft at any time to incorporate further feedback.' },
    { label: 'Return to Draft',   next: 'DRAFT',         icon: RotateCcw,    confirm: false },
  ],
  APPROVED:     [
    { label: 'Submit for Export', next: 'LOCKED',        icon: Lock,         confirm: true,
      confirmMsg: 'Submitting for export freezes figure numbering and page layout. You can revert to Approved if further revisions are needed before the final download.' },
    { label: 'Return to Draft',   next: 'DRAFT',         icon: RotateCcw,    confirm: false },
  ],
  LOCKED:       [
    { label: 'Return to Approved', next: 'APPROVED',     icon: RotateCcw,    confirm: true,
      confirmMsg: 'Returning to Approved will allow further edits before final export. Figure numbering will be recalculated when the chapter is resubmitted.' },
  ],
  FINALIZED:    [{ label: 'Reopen as Draft',  next: 'DRAFT',         icon: RotateCcw,    confirm: false }],
  REGEN_PENDING:[{ label: 'Reopen Draft',     next: 'DRAFT',         icon: RotateCcw,    confirm: false }],
  NOT_STARTED:  [],
  GENERATING:   [],
}

// ── Compact inline badge (for chapter list views) ─────────────────────────────

export function StatusBadge({ status }) {
  const cfg = CHAPTER_STATUS[status] || CHAPTER_STATUS.DRAFT
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-sans font-medium"
      style={{ fontSize: 11, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      {cfg.pulse
        ? <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color, animation: 'pulse 1.4s ease-in-out infinite' }} />
        : <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
      }
      {cfg.label}
    </span>
  )
}

// ── Confirmation dialog ───────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}
      onClick={onCancel}
    >
      <div
        style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: '1rem' }}>
          <AlertTriangle style={{ width: 18, height: 18, color: '#d97706', flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{message}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{ padding: '0.4rem 0.9rem', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', fontFamily: 'sans-serif', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{ padding: '0.4rem 0.9rem', borderRadius: 6, border: 'none', background: '#2563eb', fontFamily: 'sans-serif', fontSize: 12, color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Audit history panel ───────────────────────────────────────────────────────

function AuditHistory({ chapterId }) {
  const [log, setLog]         = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!chapterId) return
    loadChapterAuditLog(chapterId)
      .then(setLog)
      .finally(() => setLoading(false))
  }, [chapterId])

  if (loading) return <p style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Loading…</p>
  if (!log.length) return <p style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>No transitions recorded yet.</p>

  return (
    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
      {log.map(entry => (
        <div key={entry.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <Clock style={{ width: 10, height: 10, color: '#9ca3af', flexShrink: 0, marginTop: 2 }} />
          <div>
            <span style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#374151', fontWeight: 600 }}>
              {entry.previous_state} → {entry.new_state}
            </span>
            <span style={{ fontFamily: 'sans-serif', fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>
              {new Date(entry.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
            {entry.reason && (
              <p style={{ fontFamily: 'sans-serif', fontSize: 10, color: '#6b7280', marginTop: 1 }}>{entry.reason}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Full status badge with workflow action buttons ────────────────────────────

export default function ChapterStatusBadge({ thesisId, chapterId, status, onStatusChange }) {
  const [loading,        setLoading]        = useState(false)
  const [pending,        setPending]        = useState(null)  // { action } awaiting confirmation
  const [showHistory,    setShowHistory]    = useState(false)

  const cfg         = CHAPTER_STATUS[status] || CHAPTER_STATUS.DRAFT
  const transitions = TRANSITIONS[status] || []

  const executeTransition = async (action) => {
    setLoading(true)
    setPending(null)
    try {
      await applyWorkflowTransition({
        thesisId,
        chapterId,
        currentStatus: status,
        targetStatus:  action.next,
        reason:        `User action: "${action.label}"`,
      })
      // Authoritative DB re-fetch — propagates to ALL subscribers (editor, export page, overview)
      await useChapterStore.getState().refreshChapter(chapterId)
      onStatusChange?.(action.next)
      toast.success(`Status updated to ${CHAPTER_STATUS[action.next]?.label || action.next}`)
    } catch (err) {
      toast.error('Status update failed: ' + (err.message || 'unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const handleTransition = (action) => {
    if (action.confirm) {
      setPending(action)
    } else {
      executeTransition(action)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Current status */}
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg"
        style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
      >
        {loading ? (
          <Loader2 style={{ width: 13, height: 13, color: cfg.color }} className="animate-spin" />
        ) : cfg.pulse ? (
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cfg.color, animation: 'pulse 1.4s ease-in-out infinite' }} />
        ) : (
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cfg.color }} />
        )}
        <span className="font-sans text-sm font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
      </div>

      {/* Contextual info banners */}
      {(status === 'APPROVED' || status === 'FINALIZED') && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)' }}
        >
          <CheckCircle2 style={{ width: 12, height: 12, color: '#16a34a', flexShrink: 0, marginTop: 1 }} />
          <p className="font-sans text-xs" style={{ color: '#15803d' }}>
            This chapter has been finalised for export stability. Minor edits such as punctuation and spacing are still permitted. Return to Draft at any time to continue refining.
          </p>
        </div>
      )}

      {status === 'LOCKED' && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.2)' }}
        >
          <Lock style={{ width: 12, height: 12, color: '#7c3aed', flexShrink: 0, marginTop: 1 }} />
          <p className="font-sans text-xs" style={{ color: '#7c3aed' }}>
            Submitted for final review. Figure numbering and page layout are now stable for export.
          </p>
        </div>
      )}

      {status === 'UNDER_REVIEW' && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)' }}
        >
          <Eye style={{ width: 12, height: 12, color: '#d97706', flexShrink: 0, marginTop: 1 }} />
          <p className="font-sans text-xs" style={{ color: '#b45309' }}>
            Final review in progress. Approve when the chapter meets academic standards, or return to Draft to incorporate feedback.
          </p>
        </div>
      )}

      {status === 'REGEN_PENDING' && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-lg"
          style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)' }}
        >
          <AlertTriangle style={{ width: 12, height: 12, color: '#d97706', flexShrink: 0, marginTop: 1 }} />
          <p className="font-sans text-xs" style={{ color: '#92400e' }}>
            Additional refinement has been requested. Open this chapter to continue developing the content.
          </p>
        </div>
      )}

      {/* Transition buttons */}
      {transitions.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {transitions.map((t) => {
            const Icon = t.icon
            const isApprove = t.next === 'APPROVED'
            const isLock    = t.next === 'LOCKED'
            const btnColor  = isApprove ? '#16a34a' : isLock ? '#7c3aed' : '#506280'
            const btnBg     = isApprove ? 'rgba(22,163,74,0.06)' : isLock ? 'rgba(124,58,237,0.06)' : 'transparent'
            const btnBorder = isApprove ? 'rgba(22,163,74,0.4)' : isLock ? 'rgba(124,58,237,0.3)' : '#DDE4EF'
            return (
              <button
                key={t.next}
                onClick={() => handleTransition(t)}
                disabled={loading}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg font-sans text-sm transition-all disabled:opacity-50"
                style={{ border: `1px solid ${btnBorder}`, color: btnColor, background: btnBg }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.8' }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
              >
                <Icon style={{ width: 13, height: 13 }} />
                {t.label}
              </button>
            )
          })}
        </div>
      )}

      {/* Audit history toggle */}
      {chapterId && (
        <button
          onClick={() => setShowHistory(v => !v)}
          className="flex items-center gap-1.5 font-sans text-xs"
          style={{ color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
        >
          {showHistory ? <ChevronUp style={{ width: 10, height: 10 }} /> : <ChevronDown style={{ width: 10, height: 10 }} />}
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
      )}

      {showHistory && <AuditHistory chapterId={chapterId} />}

      {/* Confirmation dialog */}
      {pending && (
        <ConfirmDialog
          message={pending.confirmMsg}
          onConfirm={() => executeTransition(pending)}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  )
}
